import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StoreId, ResellerId } from "@KassaroAB/shared/types/ids";
import {
  getStoreInvoicesApi,
  generateStoreInvoiceApi,
  markStoreInvoiceAsSentApi,
  getResellerInvoicesApi,
  generateResellerInvoiceApi,
  markResellerInvoiceAsSentApi,
} from "../api/invoicesApi";
import { adminResellerInvoicesBaseKey } from "./adminResellerInvoicesQuery";

export const useGetStoreInvoicesQuery = (storeId: StoreId | undefined) => {
  return useQuery({
    queryKey: ["stores", storeId, "invoices"],
    queryFn: () => getStoreInvoicesApi(storeId!),
    enabled: !!storeId,
  });
};

export const useGenerateStoreInvoiceMutation = (storeId: StoreId | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => generateStoreInvoiceApi(storeId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "invoices"] });
    },
  });
};

export const useMarkStoreInvoiceAsSentMutation = (storeId: StoreId | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => markStoreInvoiceAsSentApi(storeId!, period),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "invoices"] });
    },
  });
};

export const useGetResellerInvoicesQuery = (resellerId: ResellerId | undefined) => {
  return useQuery({
    queryKey: ["resellers", resellerId, "invoices"],
    queryFn: () => getResellerInvoicesApi(resellerId!),
    enabled: !!resellerId,
  });
};

export const useGenerateResellerInvoiceMutation = (resellerId: ResellerId | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => generateResellerInvoiceApi(resellerId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resellers", resellerId, "invoices"] });
      queryClient.invalidateQueries({ queryKey: adminResellerInvoicesBaseKey });
    },
  });
};

export const useMarkResellerInvoiceAsSentMutation = (resellerId: ResellerId | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => markResellerInvoiceAsSentApi(resellerId!, period),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resellers", resellerId, "invoices"] });
      queryClient.invalidateQueries({ queryKey: adminResellerInvoicesBaseKey });
    },
  });
};
